import { useCallback, useEffect, useRef } from 'react'

const API_BASE = import.meta.env.VITE_API_URL || ''
const HEALTH_TIMEOUT_MS = 2000
const HEALTH_POLL_INTERVAL_MS = 1000

/**
 * Polls /api/health every second with a 2s timeout.
 * Calls onRecovered() as soon as the backend responds with 200.
 * Only active when `enabled` is true (i.e., backend is known to be down).
 */
export function useBackendHealth(enabled: boolean, onRecovered: () => void) {
  const onRecoveredRef = useRef(onRecovered)
  onRecoveredRef.current = onRecovered

  const poll = useCallback(async (signal: AbortSignal): Promise<boolean> => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS)
    // Link parent signal to this controller
    signal.addEventListener('abort', () => controller.abort())
    try {
      const res = await fetch(`${API_BASE}/api/health`, { signal: controller.signal })
      clearTimeout(timeoutId)
      return res.ok
    } catch {
      clearTimeout(timeoutId)
      return false
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    const abortController = new AbortController()
    let timerId: ReturnType<typeof setTimeout>

    const run = async () => {
      const healthy = await poll(abortController.signal)
      if (abortController.signal.aborted) return
      if (healthy) {
        onRecoveredRef.current()
        return
      }
      timerId = setTimeout(run, HEALTH_POLL_INTERVAL_MS)
    }

    run()

    return () => {
      abortController.abort()
      clearTimeout(timerId)
    }
  }, [enabled, poll])
}
